import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { User as OidcUser } from 'oidc-client-ts';
import { useAuth0, User as Auth0User } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { ApiAuth } from '@services/ApiAuth';
import { merchantAdminSplitApi } from '@services/apiBase';
import { useLazyUserContextControllerRetrieveUserContextQuery } from '@services/userContextApi';
import { loginCompleted, loginStarted, logout, PortalUser } from '../auth/authSlice';
import { MessageType } from './useBroadcastChannel';
import SnackbarUtils from '../snackbar-utils';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import useMessageBroadcaster from './useMessageBroadcaster';
import usePortalAuthentication from './usePortalAuthentication';
import AuthUtils from '../auth/auth-utils';

const roleClaim = 'https://bodd.io/roles';
const merchantIdClaim = 'https://bodd.io/merchant/merchant_id';
const enrolledAuthenticatorsClaim = 'bodd/enrolled-auths';

export enum AuthenticationModes {
    Auth0 = 'auth0',
    Auth0PortalAuth = 'auth0-portalauth',
    IdentityServer = 'identityserver',
    IdentityServerPortalAuth = 'identityserver-portalauth',
}

export const { am: authMode } = (window as any).boddSettings || { am: AuthenticationModes.Auth0 };

function fromOidcUser(user: OidcUser): PortalUser {
    const portalUser = {
        userId: user.profile.sub,
        merchantId: null,
        name: user.profile.name,
        email: user.profile.email || user.profile.preferred_username,
        firstName: user.profile.given_name,
        profileImgUrl: user.profile.picture,
        roles: [],
        enrolledAuthenticators: [],
    };
    return portalUser;
}

function fromAuth0User(user: Auth0User): PortalUser {
    const portalUser = {
        userId: user.sub,
        merchantId: null,
        name: user.name,
        email: user.email,
        firstName: user.given_name,
        profileImgUrl: user.picture,
        roles: [],
        enrolledAuthenticators: undefined,
    };
    return portalUser;
}

export interface AuthWrapper {
    login: (opts?: { appState: { returnTo: string } }) => Promise<void>;
    loginSilent: () => Promise<Auth0User | OidcUser | null>;
    logout: () => void;
    isLoading: boolean;
    isAuthenticated: boolean;
    error: Error | undefined;
    getClaims: () => Promise<{ [key: string]: any } | undefined>;
    user: Auth0User | OidcUser | undefined;
}

export default function useAuthentication() {
    const navigate = useNavigate();
    const location = useLocation();

    if (authMode === AuthenticationModes.IdentityServerPortalAuth) {
        return usePortalAuthentication();
    }

    if (authMode === AuthenticationModes.Auth0PortalAuth) {
        return usePortalAuthentication();
    }

    const [retrieveUserContext] = useLazyUserContextControllerRetrieveUserContextQuery();
    const { broadcast } = useMessageBroadcaster();
    const isStateAuthenticated = useAppSelector((state) => state.auth.authenticated);

    const isAuth0 = authMode === AuthenticationModes.Auth0;

    let auth: AuthWrapper;
    if (isAuth0) {
        const {
            getAccessTokenSilently,
            isAuthenticated,
            isLoading,
            error,
            logout: handleLogout,
            loginWithRedirect,
            user,
            getIdTokenClaims,
        } = useAuth0();

        auth = {
            isAuthenticated,
            isLoading,
            error,
            login: (opts) => loginWithRedirect({
                authorizationParams: {
                    user_device_id: AuthUtils.getUserDeviceId(),
                },
                appState: opts?.appState,
            }),
            logout: () => handleLogout({ logoutParams: { returnTo: window.location.origin } }),
            getClaims: getIdTokenClaims,
            user,
            loginSilent: async () => {
                if (!user && !isLoading) {
                    if (window.location.pathname.length > 1) {
                        navigate(`/?returnTo=${window.location.pathname}`); // hacky
                    }
                }
                return null;
            },
        };

        ApiAuth.tokenFetcher = () => {
            if (isAuthenticated) {
                return getAccessTokenSilently();
            }
            return Promise.resolve('');
        };
    } else {
        const {
            removeUser,
            signinRedirect,
            signoutRedirect,
            user,
            isAuthenticated,
            isLoading,
            error,
            signinSilent,
        } = useAuth();

        const oidClogout = () => {
            removeUser();
            signoutRedirect({
                id_token_hint: user?.id_token,
                post_logout_redirect_uri: window.location.origin,
            });
        };

        ApiAuth.tokenFetcher = () => {
            if (isAuthenticated) {
                return Promise.resolve(user?.access_token ?? '');
            }
            return Promise.resolve('');
        };

        const loginSilent = () => signinSilent().then((r) => {
            if (r === null) {
                if (window.location.pathname.length > 1) {
                    navigate(`/?returnTo=${window.location.pathname}`); // hacky
                }
            }
            return r;
        }, () => null);

        auth = {
            isAuthenticated,
            isLoading,
            error,
            login: (opts) => signinRedirect({ state: opts?.appState }), // map auth0 state format...
            logout: oidClogout,
            getClaims: () => new Promise((resolve) => {
                const claims = (auth.user?.profile ?? {}) as { [key: string]: any };
                resolve(claims);
            }),
            user: user ?? undefined,
            loginSilent,
        };
    }

    const dispatch = useAppDispatch();

    const getRolesFromIdToken = (token: { [claim: string]: any }) => {
        let roles = token[roleClaim] || token.role || [];
        if (roles && !Array.isArray(roles)) { // if there is a single claim, it is currently interpreted as string.  need to look how to set as collection...
            roles = [roles];
        }
        return roles;
    };

    useEffect(() => {
        const updateAuth = async () => {
            if (auth.isLoading) {
                dispatch(loginStarted());
            } else if (auth.isAuthenticated) {
                const idToken = await auth.getClaims();
                const roles = getRolesFromIdToken(idToken!);
                const merchantId = idToken![merchantIdClaim] || idToken!.merchant_id;
                const enrolledAuthenticators = idToken![enrolledAuthenticatorsClaim];
                const userContext = await retrieveUserContext().unwrap();
                dispatch(loginCompleted({
                    user: isAuth0 ? fromAuth0User(auth.user as Auth0User) : fromOidcUser(auth.user as OidcUser),
                    roles,
                    merchantId,
                    merchantContext: userContext,
                    enrolledAuthenticators,
                }));
                dispatch(merchantAdminSplitApi.util.invalidateTags(['UserContext']));
                broadcast({ type: MessageType.Login });
            } else {
                if (auth.error && auth.error.message === 'user is blocked') {
                    SnackbarUtils.error('Your account has been locked');
                }
                dispatch(logout());
            }
        };
        if (!isStateAuthenticated && location.pathname !== 'user-logout') {
            updateAuth();
        }
    }, [auth.isAuthenticated, auth.isLoading]);

    useEffect(() => {
        const url = new URL(window.location.href);

        if (url.searchParams.has('error') && url.searchParams.get('error') === 'access_denied' && url.searchParams.get('error_description') === 'MFA challenge failed') {
            Sentry.captureMessage('Access denied error', {
                extra: {
                    error_description: url.searchParams.get('error_description'),
                    url: url.toString(),
                },
            });
            // Log the user out if MFA challenge failed
            auth.logout();
        }

        // Authsignal adds a token query string parameter to the URL after redirect from manage MFA
        // We'll remove it here
        if (url.searchParams.has('token')) {
            // Remove the token parameter
            url.searchParams.delete('token');
            // Update URL without page reload
            window.history.replaceState({}, '', url.toString());
        }
    }, []);

    return {
        isAuthenticated: auth.isAuthenticated,
        isLoading: auth.isLoading,
        login: auth.login,
        logout: auth.logout,
        error: auth.error,
        loginSilent: auth.loginSilent,
    };
}
